﻿import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import {
  Activity,
  AlertCircle,
  AlertOctagon,
  AlertTriangle,
  ArrowLeft,
  ArrowUp,
  Ban,
  Timer,
  BookOpen,
  Briefcase,
  Building2,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Circle,
  Clock,
  ExternalLink,
  FileText,
  Gift,
  Heart,
  History,
  Hourglass,
  KeyRound,
  Mail,
  MapPin,
  Minus,
  Pencil,
  Phone,
  Plus,
  RotateCcw,
  Save,
  ShieldCheck,
  Sparkles,
  Umbrella,
  User as UserIcon,
  UserCircle2,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import {
  ALL_ROLE_CODES,
  PERMISSIONS,
  type ApiError,
  type DailyAttendanceRecord,
  type LeaveBalance,
  type LeaveBalanceAdjustment,
  type LeaveRequest,
  type ModuleCategory,
  type ModuleCode,
  type ModuleInfo,
  type PaginatedResponse,
  type RoleCode,
  type ShortLeaveRequest,
} from '@pms/shared';
import { usersService, type StaffDetail } from '@/services/users.service';
import { formatDateDMY, formatDateTimeIST } from '@/lib/dateFormat';
import { SalaryGate } from '@/components/payroll/SalaryGate';
import { StaffSalaryTab } from './StaffSalaryTab';
import { StaffLoansTab } from './StaffLoansTab';
import { StaffPerformanceTab } from './StaffPerformanceTab';
import { StaffDocumentsTab } from './StaffDocumentsTab';
import { attendanceService } from '@/services/attendance.service';
import { PunchTimelineModal } from '@/pages/attendance/PunchTimelineModal';
import { penaltyService } from '@/services/penalty.service';
import { leaveService } from '@/services/leave.service';
import { rolesService } from '@/services/roles.service';
import { modulesService } from '@/services/modules.service';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { SectionHeader } from '@/components/ui/section-header';
import { StatTile, type StatTileTone } from '@/components/ui/stat-tile';
import { Checkbox } from '@/components/ui/checkbox';
import { DataTable, type Column } from '@/components/ui/data-table';
import { Input } from '@/components/ui/input';
import { Tabs } from '@/components/ui/tabs';
import { usePermission } from '@/hooks/usePermission';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';

function getErrorMessage(e: unknown): string {
  const ax = e as AxiosError<ApiError>;
  return ax.response?.data?.error?.message ?? 'Something went wrong';
}

const TAB_KEYS = [
  'profile',
  'attendance',
  'salary',
  'loans',
  'leaves',
  'performance',
  'documents',
  'access',
] as const;
type TabKey = (typeof TAB_KEYS)[number];

const TAB_LABELS: Record<TabKey, string> = {
  profile: 'Profile',
  attendance: 'Attendance',
  salary: 'Salary',
  loans: 'Loans',
  leaves: 'Leaves',
  performance: 'Performance',
  documents: 'Documents',
  access: 'Access',
};

export function StaffDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialTab = (searchParams.get('tab') as TabKey) || 'profile';
  const [activeTab, setActiveTab] = useState<TabKey>(
    TAB_KEYS.includes(initialTab) ? initialTab : 'profile',
  );

  const { data, isLoading } = useQuery({
    queryKey: ['staff', id],
    queryFn: () => usersService.get(id),
    enabled: !!id,
  });

  const onTabChange = (key: string) => {
    setActiveTab(key as TabKey);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (key === 'profile') next.delete('tab');
      else next.set('tab', key);
      return next;
    });
  };

  if (isLoading || !data) {
    return <div className="text-muted-foreground">Loading…</div>;
  }

  return (
    <div className="space-y-3">
      <Link
        to="/hrms/staff/list"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Employee Profile
      </Link>

      <StaffHeaderCard staff={data} />

      <Tabs
        tabs={TAB_KEYS.map((k) => ({ key: k, label: TAB_LABELS[k] }))}
        active={activeTab}
        onChange={onTabChange}
      />

      <div className="space-y-3">
        {activeTab === 'profile' && <ProfileTab staff={data} />}
        {activeTab === 'attendance' && (
          <AttendanceTab
            id={id}
            staffName={`${data.user.first_name ?? ''} ${data.user.last_name ?? ''}`.trim() || data.user.employee_code || 'Employee'}
            photoUrl={data.new_photos_url ?? data.profile?.avatar_url ?? undefined}
          />
        )}
        {/* Salary, Loans and Documents all carry personal financial or identity
            records, so the same pass key covers all three. Gated here rather than
            inside each tab so the rule stays in one place. */}
        {activeTab === 'salary' && (
          <SalaryGate label="Salary">
            <StaffSalaryTab
              userId={id}
              employeeName={`${data.user.first_name ?? ''} ${data.user.last_name ?? ''}`.trim()}
            />
          </SalaryGate>
        )}
        {activeTab === 'loans' && (
          <SalaryGate label="Loans">
            <StaffLoansTab userId={id} />
          </SalaryGate>
        )}
        {activeTab === 'leaves' && <StaffLeavesTab id={id} />}
        {activeTab === 'performance' && <StaffPerformanceTab userId={id} />}
        {activeTab === 'documents' && (
          <SalaryGate label="Documents">
            <StaffDocumentsTab userId={id} />
          </SalaryGate>
        )}
        {activeTab === 'access' && (
          <AccessTab staff={data} id={id} />
        )}
      </div>
    </div>
  );
}

// ============================================================
// Header card — name, code, badges, status, edit button
// ============================================================
function StaffHeaderCard({ staff }: { staff: StaffDetail }) {
  const { user, roles } = staff;
  return (
    <Card>
      <CardContent className="p-4 sm:p-5">
    <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border/60 bg-muted">
              {(staff.new_photos_url ?? staff.profile?.avatar_url) ? (
                <img src={(staff.new_photos_url ?? staff.profile?.avatar_url)!} alt="" className="h-full w-full object-cover" />
              ) : (
                <UserCircle2 className="h-9 w-9 text-muted-foreground" />
              )}
            </div>
            <div>
              <h2 className="text-xl font-bold uppercase tracking-tight text-primary">
                {user.first_name} {user.last_name}
              </h2>
              <div className="text-xs text-muted-foreground">{user.employee_code ?? '—'}</div>
              <div className="mt-1.5 flex flex-wrap items-center gap-3 text-sm">
                <span className="inline-flex items-center gap-1 text-muted-foreground">
                  <Mail className="h-3.5 w-3.5" /> {user.email}
                </span>
                {user.phone && (
                  <span className="inline-flex items-center gap-1 text-muted-foreground">
                    <Phone className="h-3.5 w-3.5" /> {user.phone}
                  </span>
                )}
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {user.department_id && (
                  <Badge icon={<Building2 className="h-3 w-3" />}>Department</Badge>
                )}
                {user.designation_id && (
                  <Badge icon={<Briefcase className="h-3 w-3" />}>Designation</Badge>
                )}
                {roles.map((r) => (
                  <Badge key={r} icon={<UserCircle2 className="h-3 w-3" />}>
                    {r}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <span
              className={
                user.is_active
                  ? 'inline-flex rounded-full bg-primary px-3 py-0.5 text-xs font-medium text-primary-foreground'
                  : 'inline-flex rounded-full bg-muted px-3 py-0.5 text-xs font-medium text-muted-foreground'
              }
            >
              {user.is_active ? 'active' : 'inactive'}
            </span>
            <Button asChild size="sm" variant="outline">
              <Link to={`/hrms/staff/${user.id}/edit`}>
                <Pencil className="h-3.5 w-3.5" /> Edit
              </Link>
            </Button>
          </div>
    </div>
      </CardContent>
    </Card>
  );
}

function Badge({ children, icon }: { children: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
      {icon}
      {children}
    </span>
  );
}

// ============================================================
// Profile tab — Personal / Family / Address / Employment
// ============================================================
function ProfileTab({ staff }: { staff: StaffDetail }) {
  const { user, profile } = staff;
  const formatShiftTime = (t: string) => {
    const [h, m] = t.split(':').map(Number);
    const suffix = (h! >= 12) ? 'PM' : 'AM';
    return `${(h! % 12) || 12}:${String(m!).padStart(2, '0')} ${suffix}`;
  };

  return (
    <div className="space-y-4">
      <SectionCard icon={<UserIcon className="h-4 w-4" />} title="Personal Details">
        <KVGrid
          rows={[
            ['Date of Birth', formatDateDMY(profile?.dob)],
            ['Gender', profile?.gender ?? 'N/A'],
            ['Blood Group', profile?.blood_group ?? 'N/A'],
            ['Marital Status', profile?.marital_status ?? 'N/A'],
            ['Emergency Contact', profile?.emergency_contact ?? 'N/A'],
            ['Physically Challenged', profile ? (profile.physically_challenged ? 'Yes' : 'No') : 'N/A'],
            ['Phone', user.phone ?? 'N/A'],
          ]}
        />
      </SectionCard>

      <SectionCard icon={<Heart className="h-4 w-4" />} title="Family Details">
        <KVGrid
          rows={[
            ["Father's Name", profile?.father_name ?? 'N/A'],
            ["Mother's Name", profile?.mother_name ?? 'N/A'],
            ['Spouse Name', profile?.spouse_name ?? 'N/A'],
          ]}
        />
      </SectionCard>

      <SectionCard icon={<MapPin className="h-4 w-4" />} title="Address">
        <KVGrid
          rows={[
            ['Address Line 1', profile?.address_line1 ?? 'N/A'],
            ['Address Line 2', profile?.address_line2 ?? 'N/A'],
            ['City', profile?.city ?? 'N/A'],
            ['State', profile?.state ?? 'N/A'],
            ['Postal Code', profile?.postal_code ?? 'N/A'],
          ]}
        />
      </SectionCard>

      <SectionCard icon={<Briefcase className="h-4 w-4" />} title="Employment Details">
        <KVGrid
          rows={[
            ['Joining Date', formatDateDMY(profile?.joining_date)],
            ['Shift Start', profile?.work_start_time ? formatShiftTime(profile.work_start_time) : 'Default (09:00 AM)'],
            ['Shift End', profile?.work_end_time ? formatShiftTime(profile.work_end_time) : 'Default (06:00 PM)'],
            ['Status', user.is_active ? 'Active' : 'Inactive'],
            ['Last Login', formatDateDMY(user.last_login_at)],
            ['Created', formatDateDMY(user.created_at)],
          ]}
        />
      </SectionCard>
    </div>
  );
}

function SectionCard({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <SectionHeader icon={icon} title={title} />
      <CardContent className="p-4">{children}</CardContent>
    </Card>
  );
}

function KVGrid({ rows }: { rows: Array<[string, string | null]> }) {
  return (
    <div className="grid grid-cols-1 gap-x-4 gap-y-2.5 sm:grid-cols-2 sm:gap-x-6 sm:gap-y-3 md:grid-cols-3">
      {rows.map(([label, value]) => (
        <div key={label}>
          <div className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
            {label}
          </div>
          <div className="mt-0.5 text-sm font-medium">{value || 'N/A'}</div>
        </div>
      ))}
    </div>
  );
}

// ============================================================
// Placeholder for unbuilt modules
// ============================================================
// ============================================================
// Attendance tab — monthly calendar + KPI stats for one employee
// ============================================================

const STATUS_META: Record<
  DailyAttendanceRecord['status'],
  { label: string; bg: string; text: string; bar: string; border: string }
> = {
  present:        { label: 'P',   bg: 'bg-teal-100 dark:bg-teal-900/30',     text: 'text-teal-700 dark:text-teal-300',    bar: 'bg-teal-500',   border: 'border-teal-200 dark:border-teal-800/50' },
  late:           { label: 'L',   bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-300', bar: 'bg-yellow-500', border: 'border-yellow-200 dark:border-yellow-800/50' },
  half_day:       { label: 'HD',  bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-300', bar: 'bg-orange-400', border: 'border-orange-200 dark:border-orange-800/50' },
  absent:         { label: 'A',   bg: 'bg-red-100 dark:bg-red-900/30',       text: 'text-red-600 dark:text-red-400',      bar: 'bg-red-500',    border: 'border-red-200 dark:border-red-800/50' },
  leave:          { label: 'LV',  bg: 'bg-violet-100 dark:bg-violet-900/30', text: 'text-violet-700 dark:text-violet-300', bar: 'bg-violet-500', border: 'border-violet-200 dark:border-violet-800/50' },
  holiday:        { label: 'H',   bg: 'bg-violet-100 dark:bg-violet-900/30', text: 'text-violet-700 dark:text-violet-300', bar: 'bg-violet-500', border: 'border-violet-200 dark:border-violet-800/50' },
  work_from_home: { label: 'WFH', bg: 'bg-violet-100 dark:bg-violet-900/30', text: 'text-violet-700 dark:text-violet-300', bar: 'bg-violet-500', border: 'border-violet-200 dark:border-violet-800/50' },
};

// A company-paid day (approved employee_paid_hours) arrives from the API as a
// 'holiday' row carrying paid_hours_approved. It gets its own teal treatment so it
// reads as "paid by the company", not as a worked day and not as an absence.
const COMPANY_PAID_META = {
  label: 'CP',
  bg:     'bg-teal-50 dark:bg-teal-900/25',
  text:   'text-teal-700 dark:text-teal-300',
  bar:    'bg-teal-400',
  border: 'border-teal-200 dark:border-teal-800/50',
} as const;

const paidHoursOf = (r: DailyAttendanceRecord | undefined): number =>
  Number(r?.paid_hours_approved ?? 0);

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'] as const;
const DOW_LABELS  = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'] as const;

function fmtMins(totalMins: number, prefix = ''): string {
  const h = Math.floor(Math.abs(totalMins) / 60);
  const m = Math.abs(totalMins) % 60;
  return `${prefix}${h}h ${m}m`;
}

/** 1 → "1st", 2 → "2nd", 4 → "4th", 11 → "11th", 22 → "22nd". */
function ordinal(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  const suffix = n % 10 === 1 ? 'st' : n % 10 === 2 ? 'nd' : n % 10 === 3 ? 'rd' : 'th';
  return `${n}${suffix}`;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

/** "08:30:00" → "8:30 AM". Short leave times are plain TIME strings, not instants. */
function hhmmTo12(t: string): string {
  const [h = 0, m = 0] = t.split(':').map(Number);
  const suffix = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')} ${suffix}`;
}

/** One leave to list on a day's hover card. */
interface LeaveLine {
  label:  string;
  color:  string;
  timing: string;
  status: LeaveRequest['status'];
}

/** Non-approved leaves carry a tag so the card never implies they are in force. */
const LEAVE_STATUS_TAG: Partial<Record<LeaveLine['status'], string>> = {
  pending:  'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  rejected: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
};

function AttendanceTab({ id, staffName, photoUrl }: { id: string; staffName: string; photoUrl?: string }) {
  const { has } = usePermission();
  // Date whose punch log modal is open; null when closed.
  const [punchDate, setPunchDate] = useState<string | null>(null);
  const [viewMonth, setViewMonth] = useState<{ year: number; month: number }>(() => {
    const n = new Date();
    return { year: n.getFullYear(), month: n.getMonth() + 1 };
  });

  const { year, month } = viewMonth;
  const daysInMonth = new Date(year, month, 0).getDate();
  const date_from   = `${year}-${String(month).padStart(2, '0')}-01`;
  const date_to     = `${year}-${String(month).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`;

  const today     = new Date().toISOString().slice(0, 10);
  const currentYM = new Date().toISOString().slice(0, 7);

  const { data, isLoading } = useQuery({
    queryKey: ['staff-attendance', id, date_from, date_to],
    queryFn:  () => attendanceService.listDaily({ user_id: id, date_from, date_to, limit: 100 }),
  });

  // Late-arrival grace from Penalty Config: a punch-in is only "late" when it
  // exceeds this threshold (matches penalty auto-generation). Defaults to 0
  // (any lateness counts) until the config loads.
  const { data: penaltyConfig } = useQuery({
    queryKey: ['penalty', 'config'],
    queryFn:  () => penaltyService.getConfig(),
  });
  const lateGrace = Number(penaltyConfig?.late_arrival_min_threshold ?? 0);

  // Real penalties for this employee (same source the Salary tab deducts from).
  // The PENALTY card reflects actual penalty minutes, not raw lateness.
  // Scoped to the viewed month: an all-time "newest 100" would silently drop an older
  // month's rows once someone has more than 100 penalties.
  const { data: penaltyData } = useQuery({
    queryKey: ['staff-penalties', id, date_from, date_to],
    queryFn:  () => penaltyService.list({ user_id: id, status: 'approved', date_from, date_to, page: 1, limit: 100 }),
  });
  const realPenaltyMins = useMemo(() => {
    return (penaltyData?.data ?? [])
      .filter((p) => p.status === 'approved' && p.attendance_date >= date_from && p.attendance_date <= date_to)
      .reduce((s, p) => s + p.penalty_minutes, 0);
  }, [penaltyData, date_from, date_to]);

  // Approved penalties per day (YYYY-MM-DD) for the calendar hover card.
  const penaltiesByDate = useMemo(() => {
    const m = new Map<string, NonNullable<typeof penaltyData>['data']>();
    for (const p of penaltyData?.data ?? []) {
      if (p.status !== 'approved') continue;
      const key = p.attendance_date.slice(0, 10);
      const list = m.get(key);
      if (list) list.push(p);
      else m.set(key, [p]);
    }
    return m;
  }, [penaltyData]);

  // Every leave touching this month, for the hover card's LEAVES section.
  //
  // Gated on LEAVE_VIEW_ALL: this tab only demands ATTENDANCE_VIEW_ALL, so a user
  // with attendance access but not leave access must get a card without the
  // section rather than a 403.
  //
  // The window is deliberately WIDER than the month. listAllRequests filters by
  // containment (start_date >= from AND end_date <= to), so a leave running
  // 28 Sep → 3 Oct belongs to neither September nor October. Fetching the
  // surrounding months and narrowing per-day below is what makes it visible.
  const canViewLeaves = has(PERMISSIONS.LEAVE_VIEW_ALL);
  const leaveWindowFrom = `${month === 1 ? year - 1 : year}-${String(month === 1 ? 12 : month - 1).padStart(2, '0')}-01`;
  const leaveWindowTo   = (() => {
    const y = month === 12 ? year + 1 : year;
    const m = month === 12 ? 1 : month + 1;
    return `${y}-${String(m).padStart(2, '0')}-${String(new Date(y, m, 0).getDate()).padStart(2, '0')}`;
  })();

  const { data: leaveReqData } = useQuery({
    queryKey: ['staff-attendance-leaves', id, leaveWindowFrom, leaveWindowTo],
    queryFn:  () => leaveService.listAllRequests({
      user_id: id, date_from: leaveWindowFrom, date_to: leaveWindowTo, limit: 100,
    }),
    enabled: canViewLeaves,
  });

  const { data: shortLeaveData } = useQuery({
    queryKey: ['staff-attendance-short-leaves', id, leaveWindowFrom, leaveWindowTo],
    queryFn:  () => leaveService.listAllShortLeaves({
      user_id: id, date_from: leaveWindowFrom, date_to: leaveWindowTo, limit: 100,
    }),
    enabled: canViewLeaves,
  });

  /** date (YYYY-MM-DD) → the leaves to list on that day's hover card. */
  const leavesByDate = useMemo(() => {
    const m = new Map<string, LeaveLine[]>();
    const add = (key: string, line: LeaveLine) => {
      const list = m.get(key);
      if (list) list.push(line);
      else m.set(key, [line]);
    };

    // A cancelled request was withdrawn and never happened — it is not shown.
    for (const r of leaveReqData?.data ?? []) {
      if (r.status === 'cancelled') continue;
      const line: LeaveLine = {
        label:  r.leave_type_name ?? 'Leave',
        color:  r.leave_type_color ?? '#6B7280',
        timing: r.is_half_day ? `half day (${r.half_day_period ?? 'half'})` : 'full day',
        status: r.status,
      };
      // A multi-day leave lands on every date it spans.
      const end = new Date(r.end_date.slice(0, 10) + 'T00:00:00').getTime();
      for (let d = new Date(r.start_date.slice(0, 10) + 'T00:00:00'); d.getTime() <= end; d.setDate(d.getDate() + 1)) {
        add(
          `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
          line,
        );
      }
    }

    // Short leaves carry no leave type (short_leave_requests has no leave_type_id),
    // so they get a fixed label and the page's teal.
    for (const s of shortLeaveData?.data ?? []) {
      if (s.status === 'cancelled') continue;
      add(s.request_date.slice(0, 10), {
        label:  'Short leave',
        color:  '#14B8A6',
        timing: `${hhmmTo12(s.from_time)} - ${hhmmTo12(s.to_time)}`,
        status: s.status,
      });
    }
    return m;
  }, [leaveReqData, shortLeaveData]);

  // Late days that approved leave excuses — decided by the backend (the same
  // isLatenessExcusedByLeave the penalty engine uses), never recomputed here.
  // A morning half-day leave, or a short leave covering the shift-start → punch-in
  // gap, makes the day read as present instead of late.
  const { data: lateExcuseData } = useQuery({
    queryKey: ['staff-late-excuses', id, date_from, date_to],
    queryFn:  () => attendanceService.listLateExcuses({ user_id: id, date_from, date_to }),
  });
  /** date (YYYY-MM-DD) → why the lateness is excused. */
  const excusedByDate = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of lateExcuseData?.data ?? []) m.set(e.attendance_date.slice(0, 10), e.reason);
    return m;
  }, [lateExcuseData]);

  const records = (data?.data ?? []) as DailyAttendanceRecord[];

  const recordMap = useMemo(() => {
    const m = new Map<string, DailyAttendanceRecord>();
    for (const r of records) m.set(r.attendance_date.slice(0, 10), r);
    return m;
  }, [records]);

  const stats = useMemo(() => {
    let present = 0, workMins = 0, leaveDays = 0, absent = 0,
        graceMins = 0, halfDay = 0, late = 0,
        paidHours = 0, paidDays = 0;
    for (const r of records) {
      // Company-paid hours are deliberately kept out of `present` and `workMins`:
      // payroll credits those hours separately, so counting them as worked time here
      // would double-count them in every hours total on this page.
      const ph = paidHoursOf(r);
      if (ph > 0) { paidHours += ph; paidDays += 1; }
      if (r.status === 'present' || r.status === 'work_from_home') present += 1;
      if (r.status === 'late')     present += 1;
      if (r.status === 'half_day') { present += 0.5; halfDay++; }
      if (r.status === 'leave')    leaveDays++;
      // Late = a punch-in beyond the Penalty Config grace window. The backend
      // only sets status='late' for ≥4h days; a half-day that arrived late stays
      // status='half_day' but still carries late_by_minutes (the "HD + L" case).
      // Using late_by_minutes with the configured grace catches both.
      //
      // Days excused by approved leave are dropped. That verdict is FETCHED from
      // the backend (/attendance/late-excuses), not recomputed — the rule for
      // what leave excuses lateness still lives only in penalty-eligibility.ts
      // and must never be mirrored here.
      //
      // A remaining late day is still an ATTENDANCE fact, not a penalty count: it
      // may sit inside the configured waiting period and carry no penalty yet.
      // The PENALTY card above reports the real, approved penalty minutes.
      if (r.late_by_minutes > lateGrace && !excusedByDate.has(r.attendance_date.slice(0, 10))) late++;
      workMins  += r.total_working_minutes;
      graceMins += r.grace_minutes;
    }

    // Derive absent: a working day (Mon–Sat) strictly before today with no record
    // counts as absent. Sunday is the company weekly-off (payroll convention);
    // there is no holiday calendar in the system. Explicit `absent` rows also count.
    for (let d = 1; d <= daysInMonth; d++) {
      const ds = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      if (ds >= today) continue;                                   // today & future are never absent
      if (new Date(year, month - 1, d).getDay() === 0) continue;   // Sunday off
      const rec = recordMap.get(ds);
      if (!rec || rec.status === 'absent') absent++;
    }

    return { present, workMins, leaveDays, absent, graceMins, halfDay, late, paidHours, paidDays };
  }, [records, recordMap, year, month, daysInMonth, today, lateGrace, excusedByDate]);

  const prevMonth = () =>
    setViewMonth(({ year: y, month: mo }) =>
      mo === 1 ? { year: y - 1, month: 12 } : { year: y, month: mo - 1 },
    );
  const nextMonth = () =>
    setViewMonth(({ year: y, month: mo }) =>
      mo === 12 ? { year: y + 1, month: 1 } : { year: y, month: mo + 1 },
    );

  const viewYM       = `${year}-${String(month).padStart(2, '0')}`;
  const isNextCapped = viewYM >= currentYM;

  const firstDow = new Date(year, month - 1, 1).getDay(); // 0 = Sun
  const cells: Array<number | null> = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const STAT_CARDS: { label: string; value: string; sub?: string; icon: LucideIcon; tone: StatTileTone }[] = [
    { label: 'PRESENT',        value: String(stats.present),         icon: CheckCircle2,  tone: 'teal' },
    { label: 'WORK HRS',       value: fmtMins(stats.workMins),       icon: Clock,         tone: 'blue' },
    { label: 'APPROVED LEAVE', value: String(stats.leaveDays),       icon: Umbrella,      tone: 'orange' },
    { label: 'HALF DAY',       value: String(stats.halfDay),         icon: Hourglass,     tone: 'amber' },
    { label: 'LATE DAYS',      value: String(stats.late),            icon: AlertTriangle, tone: 'yellow' },
    { label: 'ABSENT',         value: String(stats.absent),          icon: Ban,           tone: 'red' },
    { label: 'PENALTY',        value: fmtMins(realPenaltyMins),      icon: AlertOctagon,  tone: 'rose' },
    { label: 'GRACE',          value: fmtMins(stats.graceMins, '+'), icon: Sparkles,      tone: 'emerald' },
  ];

  // Only surfaced in months that actually have approved company-paid hours, so the
  // tile row stays at its usual 8 across for every other month.
  if (stats.paidDays > 0) {
    STAT_CARDS.push({
      label: 'COMPANY PAID',
      value: `${stats.paidHours}h`,
      sub:   `${stats.paidDays} ${stats.paidDays === 1 ? 'day' : 'days'}`,
      icon:  Gift,
      tone:  'teal',
    });
  }
  const tileGridCols = STAT_CARDS.length > 8 ? 'lg:grid-cols-9' : 'lg:grid-cols-8';

  const monthNav = (
    <>
      <button
        onClick={prevMonth}
        className="flex h-7 w-7 items-center justify-center rounded-md border border-border/60 transition-colors hover:bg-muted/50"
      >
        <ChevronLeft className="h-3.5 w-3.5" />
      </button>
      <span className="min-w-[88px] text-center text-sm font-medium text-foreground">
        {MONTH_NAMES[month - 1]} {year}
      </span>
      <button
        onClick={nextMonth}
        disabled={isNextCapped}
        className="flex h-7 w-7 items-center justify-center rounded-md border border-border/60 transition-colors hover:bg-muted/50 disabled:cursor-not-allowed disabled:opacity-40"
      >
        <ChevronRight className="h-3.5 w-3.5" />
      </button>
    </>
  );

  return (
    <div className="space-y-4">
      {/* ── Stats + month nav ── */}
      <Card>
        <SectionHeader icon={<Activity />} title="Monthly Attendance" action={monthNav} />
        <CardContent className="p-4">

          {isLoading ? (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-8">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="h-[72px] animate-pulse rounded-xl bg-muted/40" />
              ))}
            </div>
          ) : (
            <div className={`grid grid-cols-2 gap-2 sm:grid-cols-4 ${tileGridCols}`}>
              {STAT_CARDS.map((c) => (
                <StatTile key={c.label} label={c.label} value={c.value} sub={c.sub} icon={c.icon} tone={c.tone} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Calendar grid ── */}
      <Card>
        <SectionHeader icon={<CalendarDays />} title="Daily Summary" />
        <CardContent className="p-4">
          {isLoading ? (
            <div className="grid grid-cols-7 gap-1.5">
              {Array.from({ length: 35 }).map((_, i) => (
                <div key={i} className="h-[72px] animate-pulse rounded-lg bg-muted/40" />
              ))}
            </div>
          ) : (
            <>
              {/* Day-of-week header */}
              <div className="mb-1.5 grid grid-cols-7 gap-1.5">
                {DOW_LABELS.map((d) => (
                  <div
                    key={d}
                    className="py-1 text-center text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
                  >
                    {d}
                  </div>
                ))}
              </div>

              {/* Day cells */}
              <div className="grid grid-cols-7 gap-1.5">
                {cells.map((day, idx) => {
                  if (day === null) return <div key={`e-${idx}`} />;

                  const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                  const rec     = recordMap.get(dateStr);
                  const isToday = dateStr === today;
                  const future  = dateStr > today;
                  const isSunday = new Date(year, month - 1, day).getDay() === 0;

                  // A past Mon–Sat day with no record is a derived absence (see stats memo).
                  const derivedAbsent = !rec && !future && !isToday && !isSunday;
                  // Approved company-paid hours arrive as a 'holiday' row and take over
                  // the cell — this is what stops a paid holiday showing as "A".
                  const paidHrs = paidHoursOf(rec);

                  // Approved leave can excuse the lateness (backend verdict, see the
                  // stats memo). A 'late' day then reads as a plain present day; a
                  // sub-4h day keeps its HD and only loses the "+ L".
                  //
                  // Only the verdict is used, not its text: the LEAVES section below
                  // already names the leave, so printing the reason repeated it.
                  const lateExcused = !!rec && excusedByDate.has(dateStr);
                  const shownStatus  = rec
                    ? (lateExcused && rec.status === 'late' ? 'present' : rec.status)
                    : null;

                  const meta = paidHrs > 0
                    ? COMPANY_PAID_META
                    : shownStatus
                      ? STATUS_META[shownStatus]
                      : derivedAbsent ? STATUS_META.absent : null;

                  const displayLabel = paidHrs > 0
                    ? COMPANY_PAID_META.label
                    : rec && meta
                      ? (rec.status === 'half_day' && rec.late_by_minutes > lateGrace && !lateExcused ? 'HD + L' : meta.label)
                      : derivedAbsent ? 'A' : null;

                  const DOW = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
                  const dateObj = new Date(dateStr + 'T00:00:00');
                  const tooltipDate = `${DOW[dateObj.getDay()]}, ${String(day).padStart(2,'0')} ${MONTH_NAMES[month-1]}`;

                  // Every past day and today opens its punch log (view only); future days stay inert.
                  const canOpen = !future;
                  const dayPenalties = penaltiesByDate.get(dateStr) ?? [];
                  const dayLeaves    = leavesByDate.get(dateStr) ?? [];

                  // Rendered on punched AND no-punch days alike — a full-day leave
                  // writes no attendance row, so the days that most need explaining
                  // are exactly the ones with no punch.
                  const leavesBlock = dayLeaves.length > 0 ? (
                    <div className="mt-1 space-y-0.5 border-t border-border pt-1">
                      {dayLeaves.map((l, i) => (
                        <div key={`${l.label}-${l.timing}-${i}`} className="flex items-start gap-1.5">
                          {/* Colour lives in the dot only — light leave-type colours fail contrast as text */}
                          <span
                            className="mt-1 h-2 w-2 shrink-0 rounded-full"
                            style={{ backgroundColor: l.color }}
                          />
                          <span className="min-w-0 flex-1">
                            <span className="font-medium text-foreground">{l.label}</span>
                            <span className="text-muted-foreground"> · {l.timing}</span>
                          </span>
                          {LEAVE_STATUS_TAG[l.status] && (
                            <span className={`shrink-0 rounded px-1 py-0.5 text-[9px] font-bold uppercase ${LEAVE_STATUS_TAG[l.status]}`}>
                              {l.status}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : null;

                  return (
                    <div
                      key={day}
                      role={canOpen ? 'button' : undefined}
                      tabIndex={canOpen ? 0 : undefined}
                      aria-label={canOpen ? `View punch log for ${tooltipDate}` : undefined}
                      onClick={canOpen ? () => setPunchDate(dateStr) : undefined}
                      onKeyDown={canOpen ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPunchDate(dateStr); }
                      } : undefined}
                      className={[
                        'group relative flex min-h-[60px] sm:min-h-[80px] flex-col rounded-lg sm:rounded-xl border p-1 sm:p-2 transition-all',
                        canOpen ? 'cursor-pointer hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary' : '',
                        isToday
                          ? 'border-primary ring-2 ring-primary/25 shadow-sm'
                          : meta
                            ? meta.border
                            : future
                              ? 'border-border/15'
                              : 'border-border/25',
                        meta
                          ? meta.bg
                          : future
                            ? 'bg-transparent opacity-40'
                            : 'bg-muted/20',
                      ].join(' ')}
                    >
                      {/* Hover tooltip */}
                      {meta && (rec || derivedAbsent) && (
                        <div className={`pointer-events-none absolute bottom-full left-1/2 z-30 mb-1.5 hidden ${dayLeaves.length > 0 ? 'w-64' : dayPenalties.length > 0 ? 'w-52' : 'w-44'} -translate-x-1/2 rounded-lg border bg-popover p-2.5 text-xs shadow-lg group-hover:block`}>
                          <div className="mb-1.5 flex items-center justify-between gap-1">
                            <span className="font-semibold text-foreground">{tooltipDate}</span>
                            <span className={`rounded px-1 py-0.5 text-[9px] font-bold uppercase ${meta.bg} ${meta.text}`}>
                              {paidHrs > 0 ? 'company paid' : shownStatus ? shownStatus.replace(/_/g,' ') : 'absent'}
                            </span>
                          </div>
                          {paidHrs > 0 && (
                            <div className="mt-0.5 flex items-start gap-1 text-teal-700 dark:text-teal-300">
                              <Gift className="mt-0.5 h-3 w-3 flex-shrink-0" />
                              <span>
                                <span className="font-semibold">{paidHrs}h paid</span>
                                {rec?.remarks && <span className="opacity-75"> — {rec.remarks}</span>}
                              </span>
                            </div>
                          )}
                          {rec ? (
                            <>
                              {rec.first_punch_in && (
                                <div className="mt-0.5 flex items-center gap-1 text-muted-foreground">
                                  <Clock className="h-3 w-3 flex-shrink-0" />
                                  <span>{formatTime(rec.first_punch_in)} → {rec.last_punch_out ? formatTime(rec.last_punch_out) : '—'}</span>
                                </div>
                              )}
                              {rec.total_working_minutes > 0 && (
                                <div className="mt-0.5 flex items-center gap-1 text-muted-foreground">
                                  <Timer className="h-3 w-3 flex-shrink-0" />
                                  <span>Work: {fmtMins(rec.total_working_minutes)}</span>
                                </div>
                              )}
                              {rec.grace_minutes > 0 && (
                                <div className="mt-0.5 flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                                  <ArrowUp className="h-3 w-3 flex-shrink-0" />
                                  <span>Grace: +{fmtMins(rec.grace_minutes)}</span>
                                </div>
                              )}
                              {leavesBlock}
                              {rec.total_working_minutes > 0 && (
                                <div className="mt-1 flex items-center gap-1 border-t border-border pt-1 font-semibold text-foreground">
                                  <Timer className="h-3 w-3 flex-shrink-0" />
                                  <span>Total: {fmtMins(rec.total_working_minutes + rec.grace_minutes)}</span>
                                </div>
                              )}
                            </>
                          ) : (
                            <>
                              <div className="mt-0.5 flex items-center gap-1 text-rose-600 dark:text-rose-400">
                                <AlertCircle className="h-3 w-3 flex-shrink-0" />
                                <span>No punch recorded</span>
                              </div>
                              {leavesBlock}
                            </>
                          )}
                          {/* Approved penalties — listed under Total, amounts from the penalty rows */}
                          {dayPenalties.length > 0 && (
                            <div className="mt-1 space-y-1 border-t border-border pt-1">
                              {dayPenalties.map((p) => {
                                // Labelled by type, not by `reason`: tier reasons are free text and can be stale.
                                const label = p.penalty_type === 'late_arrival'
                                  ? (p.occurrence_number ? `${ordinal(p.occurrence_number)} Late` : 'Late')
                                  : p.penalty_type === 'miss_punch'
                                    ? 'Miss punch'
                                    : 'Manual penalty';
                                return (
                                  <div key={p.id}>
                                    <div className="flex items-center gap-1 text-rose-600 dark:text-rose-400">
                                      <AlertOctagon className="h-3 w-3 flex-shrink-0" />
                                      <span className="min-w-0 flex-1 truncate">{label}</span>
                                      {p.penalty_minutes > 0 ? (
                                        <span className="shrink-0 font-semibold tabular-nums">−{fmtMins(p.penalty_minutes)}</span>
                                      ) : (
                                        <span className="shrink-0 text-muted-foreground">warning</span>
                                      )}
                                    </div>
                                    {p.penalty_type === 'manual' && p.reason && (
                                      <p className="ml-4 line-clamp-2 text-[10px] text-muted-foreground">{p.reason}</p>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Day number + today dot */}
                      <div className="flex items-center justify-between">
                        <span
                          className={`text-xs font-bold ${
                            isToday
                              ? 'text-primary'
                              : future
                                ? 'text-muted-foreground/40'
                                : 'text-foreground/60'
                          }`}
                        >
                          {day}
                        </span>
                        {isToday && (
                          <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                        )}
                      </div>

                      {/* Status — centred in remaining space */}
                      <div className="flex flex-1 flex-col items-center justify-center gap-0.5">
                        {meta && displayLabel && (
                          <>
                            <span className={`text-[11px] font-black leading-none ${meta.text}`}>
                              {displayLabel}
                            </span>
                            {paidHrs > 0 && (
                              <span className={`flex items-center gap-0.5 text-[10px] font-semibold leading-none ${meta.text} opacity-80`}>
                                <Gift className="h-2.5 w-2.5 flex-shrink-0" />
                                {paidHrs}h
                              </span>
                            )}
                            {rec && rec.total_working_minutes > 0 && (
                              <span className={`text-[10px] font-semibold leading-none ${meta.text} opacity-75`}>
                                {fmtMins(rec.total_working_minutes + rec.grace_minutes)}
                              </span>
                            )}
                            {rec?.first_punch_in && (
                              <span className={`text-[9px] font-medium leading-none ${meta.text} opacity-60`}>
                                {formatTime(rec.first_punch_in)}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="mt-4 flex flex-wrap gap-3 border-t border-border/30 pt-3">
                {(Object.entries(STATUS_META) as [DailyAttendanceRecord['status'], typeof STATUS_META[keyof typeof STATUS_META]][]).map(
                  ([status, m]) => (
                    <span key={status} className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                      <span className={`inline-block h-3 w-3 rounded border ${m.bg} ${m.border}`} />
                      <span className="font-semibold">{m.label}</span>
                      <span className="opacity-60">— {status.replace(/_/g, ' ')}</span>
                    </span>
                  ),
                )}
                <span className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                  <span className={`inline-block h-3 w-3 rounded border ${COMPANY_PAID_META.bg} ${COMPANY_PAID_META.border}`} />
                  <span className="font-semibold">{COMPANY_PAID_META.label}</span>
                  <span className="opacity-60">— company paid</span>
                </span>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {punchDate && (
        <PunchTimelineModal
          readOnly
          userId={id}
          userName={staffName}
          photoUrl={photoUrl}
          date={punchDate}
          onClose={() => setPunchDate(null)}
        />
      )}
    </div>
  );
}

// ============================================================
// Access tab — port of the StaffAccessPage content
// ============================================================
const CATEGORY_LABEL: Record<ModuleCategory, string> = {
  workspace: 'Workspace',
  productivity: 'Productivity',
  operations: 'Operations',
  finance_resources: 'Finance & Resources',
  teams_support: 'Teams & Support',
  system: 'System',
};

const CATEGORY_ORDER: ModuleCategory[] = [
  'workspace',
  'productivity',
  'operations',
  'finance_resources',
  'teams_support',
  'system',
];

function AccessTab({ staff, id }: { staff: StaffDetail; id: string }) {
  const queryClient = useQueryClient();
  const { data: roles } = useQuery({ queryKey: ['roles'], queryFn: () => rolesService.list() });
  const { data: catalog } = useQuery({
    queryKey: ['modules-catalog'],
    queryFn: () => modulesService.list(),
  });

  const [selectedRoles, setSelectedRoles] = useState<Set<RoleCode>>(new Set(staff.roles));
  const [selectedModules, setSelectedModules] = useState<Set<ModuleCode>>(new Set(staff.modules));
  const [initialModules, setInitialModules] = useState<Set<ModuleCode>>(new Set(staff.modules));
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);

  useEffect(() => {
    setSelectedRoles(new Set(staff.roles));
    setSelectedModules(new Set(staff.modules));
    setInitialModules(new Set(staff.modules));
  }, [staff]);

  const modulesByCategory = useMemo(() => {
    const grouped = new Map<ModuleCategory, ModuleInfo[]>();
    for (const m of catalog ?? []) {
      const arr = grouped.get(m.category) ?? [];
      arr.push(m);
      grouped.set(m.category, arr);
    }
    return grouped;
  }, [catalog]);

  const saveRoles = useMutation({
    mutationFn: () =>
      usersService.assignRoles(id, { role_codes: Array.from(selectedRoles) as RoleCode[] }),
    onSuccess: () => {
      toast.success('Roles updated. Hit "Reset to defaults" if you want modules re-seeded.');
      void queryClient.invalidateQueries({ queryKey: ['staff', id] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const saveModules = useMutation({
    mutationFn: () => {
      const add: ModuleCode[] = [];
      const remove: ModuleCode[] = [];
      for (const m of selectedModules) if (!initialModules.has(m)) add.push(m);
      for (const m of initialModules) if (!selectedModules.has(m)) remove.push(m);
      return usersService.setModules(id, { add, remove });
    },
    onSuccess: () => {
      toast.success('Module visibility saved');
      setInitialModules(new Set(selectedModules));
      void queryClient.invalidateQueries({ queryKey: ['staff', id] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const reset = useMutation({
    mutationFn: () => usersService.resetModulesToDefaults(id),
    onSuccess: async () => {
      toast.success('Modules reset to role defaults');
      const refreshed = await usersService.get(id);
      setSelectedModules(new Set(refreshed.modules));
      setInitialModules(new Set(refreshed.modules));
      void queryClient.invalidateQueries({ queryKey: ['staff', id] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const dirtyModules =
    selectedModules.size !== initialModules.size ||
    Array.from(selectedModules).some((m) => !initialModules.has(m));

  const toggleRole = (r: RoleCode) =>
    setSelectedRoles((prev) => {
      const next = new Set(prev);
      if (next.has(r)) next.delete(r);
      else next.add(r);
      return next;
    });

  const toggleModule = (m: ModuleCode) =>
    setSelectedModules((prev) => {
      const next = new Set(prev);
      if (next.has(m)) next.delete(m);
      else next.add(m);
      return next;
    });

  const toggleCategory = (cat: ModuleCategory) => {
    const inCat = (modulesByCategory.get(cat) ?? []).map((m) => m.code as ModuleCode);
    setSelectedModules((prev) => {
      const next = new Set(prev);
      const allOn = inCat.every((m) => next.has(m));
      if (allOn) for (const m of inCat) next.delete(m);
      else for (const m of inCat) next.add(m);
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <SectionCard icon={<ShieldCheck className="h-4 w-4" />} title="Roles (Capability)">
        <p className="mb-3 text-xs text-muted-foreground">
          Pick one or more. Multi-role users get the union of all role permissions.
        </p>
        <div className="flex flex-wrap gap-2">
          {ALL_ROLE_CODES.map((r) => (
            <label
              key={r}
              className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition ${
                selectedRoles.has(r)
                  ? 'border-primary bg-primary/5'
                  : 'border-border/60 hover:bg-muted/30'
              }`}
            >
              <Checkbox checked={selectedRoles.has(r)} onChange={() => toggleRole(r)} />
              <span>{(roles ?? []).find((rr) => rr.code === r)?.name ?? r}</span>
              <span className="font-mono text-xs text-muted-foreground">{r}</span>
            </label>
          ))}
        </div>
        <div className="mt-3 flex justify-end">
          <Button size="sm" onClick={() => saveRoles.mutate()} disabled={saveRoles.isPending}>
            <Save className="h-4 w-4" /> Save Roles
          </Button>
        </div>
      </SectionCard>

      <SectionCard
        icon={<KeyRound className="h-4 w-4" />}
        title={`Module Visibility ${dirtyModules ? '(unsaved changes)' : ''}`}
      >
        <p className="mb-3 text-xs text-muted-foreground">
          Check what this user can SEE in their sidebar. This is the only place per-user module
          visibility is edited.
        </p>
        <div className="space-y-4">
          {CATEGORY_ORDER.map((cat) => {
            const mods = modulesByCategory.get(cat) ?? [];
            if (mods.length === 0) return null;
            const inCat = mods.map((m) => m.code as ModuleCode);
            const checkedInCat = inCat.filter((m) => selectedModules.has(m)).length;
            return (
              <div key={cat}>
                <div className="mb-2 flex items-center gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {CATEGORY_LABEL[cat]}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    ({checkedInCat}/{inCat.length})
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 px-2 text-xs"
                    onClick={() => toggleCategory(cat)}
                  >
                    Toggle all
                  </Button>
                </div>
                <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
                  {mods.map((m) => (
                    <label
                      key={m.code}
                      className="flex items-center gap-2 rounded-md border border-border/60 px-3 py-2 text-sm hover:bg-muted/30"
                    >
                      <Checkbox
                        checked={selectedModules.has(m.code as ModuleCode)}
                        onChange={() => toggleModule(m.code as ModuleCode)}
                      />
                      <span className="flex-1 truncate">{m.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setResetConfirmOpen(true)}
            disabled={reset.isPending}
          >
            <RotateCcw className="h-4 w-4" /> Reset to role defaults
          </Button>
          <Button
            size="sm"
            onClick={() => saveModules.mutate()}
            disabled={saveModules.isPending || !dirtyModules}
          >
            <Save className="h-4 w-4" /> Save Modules
          </Button>
        </div>
      </SectionCard>

      <ConfirmDialog
        open={resetConfirmOpen}
        onOpenChange={setResetConfirmOpen}
        title="Reset modules to role defaults?"
        description="Unsaved changes will be lost. This will overwrite the current module selection with the role's defaults."
        confirmLabel="Reset"
        onConfirm={() => reset.mutate()}
      />

      <SectionCard
        icon={<KeyRound className="h-4 w-4" />}
        title="Permissions via Role (read-only)"
      >
        <p className="mb-2 text-xs text-muted-foreground">
          Derived from the role(s) above. To change a role's permission set, go to{' '}
          <Link to="/system/roles" className="underline">
            Master · Roles
          </Link>
          .
        </p>
        {staff.permissions.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {staff.roles.includes('SUPER_ADMIN' as RoleCode)
              ? 'SUPER_ADMIN — bypasses all checks (no explicit permissions).'
              : 'No explicit permissions via current role assignments.'}
          </p>
        ) : (
          <div className="flex flex-wrap gap-1">
            {staff.permissions.map((p) => (
              <span
                key={p}
                className="rounded-full border border-border/60 px-2 py-0.5 font-mono text-[10px] text-muted-foreground"
              >
                {p}
              </span>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}

// ============================================================
// Leaves tab — balances + leave history + short leave history
// ============================================================

type LeaveStatusFilter = 'all' | 'pending' | 'approved' | 'rejected';

const LEAVE_STATUS_TABS: { key: LeaveStatusFilter; label: string }[] = [
  { key: 'all',      label: 'All' },
  { key: 'pending',  label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
];

function leaveFormatDate(d: string) {
  return formatDateDMY(d);
}

function leaveFormatTime(t: string) {
  const parts = t.split(':');
  const hour = parseInt(parts[0] ?? '0', 10);
  const m = parts[1] ?? '00';
  return `${hour % 12 || 12}:${m} ${hour >= 12 ? 'PM' : 'AM'}`;
}

function leaveFormatDuration(mins: number) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} hr`;
  return `${h} hr ${m} min`;
}

function LeaveStatusBadge({ status }: { status: LeaveRequest['status'] }) {
  const map = {
    pending:   { label: 'Pending',   cls: 'bg-amber-50 text-amber-700 border-amber-200' },
    approved:  { label: 'Approved',  cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    rejected:  { label: 'Rejected',  cls: 'bg-red-50 text-red-700 border-red-200' },
    cancelled: { label: 'Cancelled', cls: 'bg-muted text-muted-foreground border-border' },
  } as const;
  const v = map[status] ?? map.cancelled;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${v.cls}`}>
      {v.label}
    </span>
  );
}

function LeaveApprovalBadge({ total, status, history }: {
  total: number;
  status: LeaveRequest['status'];
  history: { step_order: number; action: 'approved' | 'rejected' }[];
}) {
  if (total === 0) return <span className="text-xs text-muted-foreground">—</span>;
  if (status === 'approved') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-medium">
        <CheckCircle2 className="w-3 h-3" />All {total}
      </span>
    );
  }
  if (status === 'rejected') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 border border-red-200 text-red-700 text-xs font-medium">
        <XCircle className="w-3 h-3" />Rejected
      </span>
    );
  }
  if (status === 'cancelled') return <span className="text-xs text-muted-foreground">—</span>;
  const done = history.filter((h) => h.action === 'approved').length;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-teal-50 border border-teal-200 text-teal-700 text-xs font-medium">
      Step {done + 1}/{total}
    </span>
  );
}

// The Leaves tab lists every row with no pager; the API caps one page at 100
const LEAVE_FETCH_PAGE_SIZE = 100;

async function fetchAllPages<T>(fetchPage: (page: number) => Promise<PaginatedResponse<T>>): Promise<T[]> {
  const first = await fetchPage(1);
  const rows = [...first.data];
  for (let page = 2; page <= first.pagination.totalPages; page++) {
    rows.push(...(await fetchPage(page)).data);
  }
  return rows;
}

const LEAVE_ADJUST_MAX_DAYS = 60;

type LeaveAdjustAction = 'add' | 'remove';

function LeaveBalanceAdjuster({ userId, balance }: { userId: string; balance: LeaveBalance }) {
  const queryClient = useQueryClient();
  const [days, setDays] = useState('1');

  const accrued = Number(balance.accrued);
  const available = Number(balance.available);
  // Only credited days can be taken back, and never below zero available
  const removable = Math.max(0, Math.min(accrued, available));

  const amount = parseFloat(days);
  const valid =
    Number.isFinite(amount) && amount > 0 && amount <= LEAVE_ADJUST_MAX_DAYS && Number.isInteger(amount * 2);
  const canRemove = valid && amount <= removable;

  const adjust = useMutation({
    mutationFn: (vars: { action: LeaveAdjustAction; days: number }) =>
      leaveService.adjustBalance(userId, {
        leave_type_id: balance.leave_type_id,
        balance_year: balance.balance_year,
        action: vars.action,
        days: vars.days,
      }),
    onSuccess: (_data, vars) => {
      toast.success(
        vars.action === 'add'
          ? `Added ${vars.days.toFixed(1)} day(s) to ${balance.leave_type_name}`
          : `Removed ${vars.days.toFixed(1)} day(s) from ${balance.leave_type_name}`,
      );
      void queryClient.invalidateQueries({ queryKey: ['staff-leaves-balances', userId] });
      void queryClient.invalidateQueries({ queryKey: ['staff-leave-adjustments', userId] });
      setDays('1');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="mt-3 border-t border-border/40 pt-3">
      <div className="flex items-center gap-2">
        <Input
          type="number"
          min={0.5}
          step={0.5}
          max={LEAVE_ADJUST_MAX_DAYS}
          value={days}
          onChange={(e) => setDays(e.target.value)}
          aria-label={`Days to add or remove for ${balance.leave_type_name}`}
          className="h-9 w-20 text-right"
        />
        <Button
          size="sm"
          variant="outline"
          disabled={!valid || adjust.isPending}
          onClick={() => adjust.mutate({ action: 'add', days: amount })}
        >
          <Plus className="h-3.5 w-3.5" />
          Add
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="text-destructive hover:text-destructive"
          disabled={!canRemove || adjust.isPending}
          onClick={() => adjust.mutate({ action: 'remove', days: amount })}
        >
          <Minus className="h-3.5 w-3.5" />
          Remove
        </Button>
      </div>
      <p className="mt-1.5 text-[10px] text-muted-foreground">
        Can remove up to {removable.toFixed(1)} day(s)
      </p>
    </div>
  );
}

const ADJUSTMENT_COLUMNS: Column<LeaveBalanceAdjustment>[] = [
  {
    key: 'when',
    header: 'Date & Time',
    cell: (a) => <span className="text-sm text-muted-foreground whitespace-nowrap">{formatDateTimeIST(a.created_at)}</span>,
  },
  {
    key: 'type',
    header: 'Leave Type',
    cell: (a) => (
      <div className="flex items-center gap-2">
        <span
          className="w-2.5 h-2.5 rounded-full shrink-0"
          style={{ backgroundColor: a.leave_type_color ?? '#6B7280' }}
        />
        <span className="font-medium text-sm">{a.leave_type_name}</span>
        <span className="text-xs text-muted-foreground">({a.balance_year})</span>
      </div>
    ),
  },
  {
    key: 'action',
    header: 'Action',
    cell: (a) =>
      a.action === 'add' ? (
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400 whitespace-nowrap">
          <Plus className="h-3 w-3" />
          {a.days.toFixed(1)} Added
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 rounded-full bg-rose-500/15 px-2 py-0.5 text-xs font-semibold text-rose-600 dark:text-rose-400 whitespace-nowrap">
          <Minus className="h-3 w-3" />
          {a.days.toFixed(1)} Removed
        </span>
      ),
  },
  {
    key: 'accrued',
    header: 'Accrued',
    cell: (a) => (
      <span className="text-sm text-muted-foreground whitespace-nowrap">
        {a.accrued_before.toFixed(1)} → <span className="font-medium text-foreground">{a.accrued_after.toFixed(1)}</span>
      </span>
    ),
  },
  {
    key: 'available',
    header: 'Available',
    cell: (a) => (
      <span className="text-sm text-muted-foreground whitespace-nowrap">
        {a.available_before.toFixed(1)} → <span className="font-medium text-foreground">{a.available_after.toFixed(1)}</span>
      </span>
    ),
  },
  {
    key: 'by',
    header: 'Done By',
    cell: (a) => <span className="text-sm">{a.created_by_name ?? '—'}</span>,
  },
];

function LeaveAdjustmentHistory({ userId }: { userId: string }) {
  const query = useQuery({
    queryKey: ['staff-leave-adjustments', userId],
    queryFn:  () => fetchAllPages((page) =>
      leaveService.getUserBalanceAdjustments(userId, page, LEAVE_FETCH_PAGE_SIZE),
    ),
  });

  return (
    <Card>
      <SectionHeader icon={<History />} title="Balance Adjustment History" />
      <CardContent className="p-0">
        <DataTable<LeaveBalanceAdjustment>
          columns={ADJUSTMENT_COLUMNS}
          rows={query.data ?? []}
          rowKey={(a) => a.id}
          loading={query.isLoading}
          error={query.isError}
          onRetry={() => void query.refetch()}
          empty={
            <div className="flex flex-col items-center gap-2 py-6">
              <History className="w-8 h-8 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">No manual adjustments yet</p>
            </div>
          }
        />
      </CardContent>
    </Card>
  );
}

function StaffLeavesTab({ id }: { id: string }) {
  const [statusFilter, setStatusFilter] = useState<LeaveStatusFilter>('all');

  const balancesQuery = useQuery({
    queryKey: ['staff-leaves-balances', id],
    queryFn:  () => leaveService.getUserBalances(id),
  });

  const { has } = usePermission();
  const canAdjust = has(PERMISSIONS.LEAVE_ADMIN);
  // Unpaid types (Regular leave) carry no balance, so they get no box
  const paidBalances = (balancesQuery.data ?? []).filter((b) => b.is_paid);

  const requestsQuery = useQuery({
    queryKey: ['staff-leaves-regular', id, statusFilter],
    queryFn:  () => fetchAllPages((page) => leaveService.listAllRequests({
      user_id: id,
      status: statusFilter === 'all' ? undefined : statusFilter,
      page,
      sort: 'start_date',
      limit: LEAVE_FETCH_PAGE_SIZE,
    })),
  });

  const shortQuery = useQuery({
    queryKey: ['staff-leaves-short', id, statusFilter],
    queryFn:  () => fetchAllPages((page) => leaveService.listAllShortLeaves({
      user_id: id,
      status: statusFilter === 'all' ? undefined : statusFilter,
      page,
      limit: LEAVE_FETCH_PAGE_SIZE,
    })),
  });

  function handleTabChange(tab: string) {
    setStatusFilter(tab as LeaveStatusFilter);
  }

  const leaveColumns: Column<LeaveRequest>[] = [
    {
      key: 'type',
      header: 'Leave Type',
      cell: (req) => (
        <div className="flex items-center gap-2">
          <span
            className="w-2.5 h-2.5 rounded-full shrink-0"
            style={{ backgroundColor: req.leave_type_color ?? '#6B7280' }}
          />
          <span className="font-medium text-sm">{req.leave_type_name ?? '—'}</span>
        </div>
      ),
    },
    {
      key: 'period',
      header: 'Period',
      cell: (req) => (
        <span className="text-sm text-muted-foreground">
          {leaveFormatDate(req.start_date)} → {leaveFormatDate(req.end_date)}
          <span className="ml-1.5 font-medium text-foreground">{req.days_count}d</span>
          {req.is_half_day && (
            <span className="ml-1 text-xs text-muted-foreground">({req.half_day_period})</span>
          )}
        </span>
      ),
    },
    {
      key: 'reason',
      header: 'Reason',
      cell: (req) => (
        <span className="text-muted-foreground max-w-[160px] block truncate text-sm" title={req.reason}>
          {req.reason}
        </span>
      ),
    },
    {
      key: 'doc',
      header: 'Doc',
      cell: (req) =>
        req.document_url ? (
          <a href={req.document_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80">
            <ExternalLink className="w-4 h-4" />
          </a>
        ) : (
          <span className="text-muted-foreground/40"><FileText className="w-4 h-4" /></span>
        ),
    },
    {
      key: 'approval',
      header: 'Approval',
      cell: (req) => (
        <LeaveApprovalBadge
          total={req.total_approval_steps || 0}
          status={req.status}
          history={req.approval_history ?? []}
        />
      ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (req) => <LeaveStatusBadge status={req.status} />,
    },
  ];

  const shortColumns: Column<ShortLeaveRequest>[] = [
    {
      key: 'date',
      header: 'Date',
      cell: (sl) => <span className="text-sm text-muted-foreground">{leaveFormatDate(sl.request_date)}</span>,
    },
    {
      key: 'time',
      header: 'Time',
      cell: (sl) => (
        <span className="text-sm text-muted-foreground">
          {leaveFormatTime(sl.from_time)} – {leaveFormatTime(sl.to_time)}
          <span className="ml-1.5 text-xs text-teal-600">({leaveFormatDuration(sl.duration_minutes)})</span>
        </span>
      ),
    },
    {
      key: 'reason',
      header: 'Reason',
      cell: (sl) => (
        <span className="text-muted-foreground max-w-[160px] block truncate text-sm" title={sl.reason}>
          {sl.reason}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (sl) => <LeaveStatusBadge status={sl.status} />,
    },
  ];

  return (
    <div className="space-y-4">
      {/* Balance Cards */}
      <Card>
        <SectionHeader icon={<CalendarDays />} title="Leave Balances" />
        <CardContent className="p-4">
          {balancesQuery.isLoading ? (
            <div className="flex gap-3 overflow-x-auto">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-[88px] w-40 shrink-0 animate-pulse rounded-xl bg-muted/40" />
              ))}
            </div>
          ) : balancesQuery.isError ? (
            <p className="text-sm text-destructive">Failed to load balances.</p>
          ) : paidBalances.length === 0 ? (
            <p className="text-sm text-muted-foreground">No leave balances found.</p>
          ) : (
            <div className="flex flex-wrap gap-3">
              {paidBalances.map((b) => (
                <StatTile
                  key={b.leave_type_id}
                  className={canAdjust ? 'min-w-[260px]' : 'min-w-[150px]'}
                  icon={Circle}
                  color={b.leave_type_color ?? '#6B7280'}
                  label={b.leave_type_name}
                  value={Number(b.available).toFixed(1)}
                  sub={`Used ${Number(b.used).toFixed(1)} / Accrued ${Number(b.accrued).toFixed(1)}`}
                >
                  {canAdjust && <LeaveBalanceAdjuster userId={id} balance={b} />}
                </StatTile>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <LeaveAdjustmentHistory userId={id} />

      {/* Status filter */}
      <Tabs tabs={LEAVE_STATUS_TABS} active={statusFilter} onChange={handleTabChange} />

      {/* Regular Leave History */}
      <Card>
        <SectionHeader icon={<BookOpen />} title="Regular Leave" />
        <CardContent className="p-0">
          <DataTable<LeaveRequest>
            columns={leaveColumns}
            rows={requestsQuery.data ?? []}
            rowKey={(r) => r.id}
            loading={requestsQuery.isLoading}
            error={requestsQuery.isError}
            onRetry={() => void requestsQuery.refetch()}
            empty={
              <div className="flex flex-col items-center gap-2 py-6">
                <CalendarDays className="w-8 h-8 text-muted-foreground/40" />
                <p className="text-sm text-muted-foreground">
                  {statusFilter === 'all' ? 'No leave requests' : `No ${statusFilter} requests`}
                </p>
              </div>
            }
          />
        </CardContent>
      </Card>

      {/* Short Leave History */}
      <Card>
        <SectionHeader icon={<Clock />} title="Short Leave" />
        <CardContent className="p-0">
          <DataTable<ShortLeaveRequest>
            columns={shortColumns}
            rows={shortQuery.data ?? []}
            rowKey={(r) => r.id}
            loading={shortQuery.isLoading}
            error={shortQuery.isError}
            onRetry={() => void shortQuery.refetch()}
            empty={
              <div className="flex flex-col items-center gap-2 py-6">
                <Clock className="w-8 h-8 text-muted-foreground/40" />
                <p className="text-sm text-muted-foreground">
                  {statusFilter === 'all' ? 'No short leave requests' : `No ${statusFilter} short leaves`}
                </p>
              </div>
            }
          />
        </CardContent>
      </Card>
    </div>
  );
}
